import * as PIXI from "pixi.js";

import { SingleInstanceClass } from "@src/core/utils/SingleInstanceClass";
import { EventSystem } from "@src/core/events/EventSystem";
import { EGameState, TSpinResponse } from "@src/server/ResponseTypes";
import { IView } from "@src/core/game/components/IView";
import { gameEngine } from "@src/game/GameEngine";
import { constants } from "@src/game/Constants";
import { Spine } from "@pixi/spine-pixi";
import gsap from "gsap";
import { Audio } from "@src/core/audio/Audio";

export class CoinAnimator extends SingleInstanceClass implements IView {
  private _view: PIXI.Container = new PIXI.Container();
  public get view(): PIXI.Container {
    return this._view;
  }

  private _coinPool: CoinPool;
  private _coinSplashPool: CoinSplashPool;
  private _queuedCoinPositions: PIXI.Point[] = [];
  private _coinFlyEndpoints!: PIXI.Point[];
  private _coinFlyEndScale!: number;
  private _coinFlySplashScale!: number;

  public static maxLandedCoinCount: number;
  public static currentLandedCoinCount: number;

  public static maxSplashCount: number;
  public static currentSplashCount: number;

  public static shouldPlayCelebrationAnimation: boolean;

  public constructor() {
    super();

    this._coinPool = new CoinPool();
    this._coinSplashPool = new CoinSplashPool();

    CoinAnimator.maxLandedCoinCount = 0;
    CoinAnimator.currentLandedCoinCount = 0;

    CoinAnimator.maxSplashCount = 0;
    CoinAnimator.currentSplashCount = 0;

    CoinAnimator.shouldPlayCelebrationAnimation = false;

    this._startCoinToCounter = this._startCoinToCounter.bind(this);

    this._setupEventListeners();
  }

  private _setupEventListeners(): void {
    EventSystem.addListener(
      constants.GameEvents.SPECIAL_EFFECT_APPLIED, this._startCoinToCounter
    );

    EventSystem.addListener(
      constants.GameEvents.LANDSCAPE_LAYOUT_REQUESTED,
      this._onLandscapeLayoutRequested.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.PORTRAIT_LAYOUT_REQUESTED,
      this._onPortraitLayoutRequested.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.GAME_PERFORM_ACTION,
      this._onPerformedGameAction.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.SYMBOL_LANDED,
      this._onSymbolLanded.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.WINNING_POPUP_OPENED,
      this._onWinningPopupOpened.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.WINNING_POPUP_CLOSED,
      this._onWinningPopupClosed.bind(this),
    );
  }

  private _onLandscapeLayoutRequested(): void {
    this._isLandscape = true;
    this._coinFlyEndScale = 0.5;
    this._coinFlySplashScale = 1.0;

    this._coinFlyEndpoints = [
      new PIXI.Point(
        Math.floor(Math.random() * (400 - 200 + 1)) + 200,
        Math.floor(Math.random() * (-450 - -500 + 1)) + -450,
      ),
      new PIXI.Point(
        Math.floor(Math.random() * (-400 - -200 + 1)) + -200,
        Math.floor(Math.random() * (-450 - -500 + 1)) + -450,
      ),
    ];
  }

  private _onPortraitLayoutRequested(): void {
    this._isLandscape = false;
    this._coinFlyEndScale = 0.75;
    this._coinFlySplashScale = 1.5;

    this._coinFlyEndpoints = [
      new PIXI.Point(
        Math.floor(Math.random() * (400 - 200 + 1)) + 200,
        Math.floor(Math.random() * (-720 - -740 + 1)) + -720,
      ),
      new PIXI.Point(
        Math.floor(Math.random() * (-400 - -200 + 1)) + -200,
        Math.floor(Math.random() * (-720 - -740 + 1)) + -720,
      ),
    ];
  }

  private _onSymbolLanded(type: string, x: number, y: number): void {
    if (gameEngine.gameActionData) {
      switch (gameEngine.gameActionData.state) {
        case EGameState.HOLD:
          this._animateHoldCoins(x, y, 2);
          return;
      }
    }

    if (type.startsWith("100")) {
      CoinAnimator.currentLandedCoinCount++;
      const localPosition = this._view.toLocal(new PIXI.Point(x, y));
      this._queuedCoinPositions.push(
        new PIXI.Point(localPosition.x, localPosition.y),
      );

      if (
        CoinAnimator.currentLandedCoinCount === CoinAnimator.maxLandedCoinCount
      ) {
        this._queuedCoinPositions.forEach((position, index) => {
          gsap.delayedCall(index * 0.1, () => {
            this._startCoinFlyingAnimation(position);
          });
        });

        this._queuedCoinPositions = [];
      }
    }
  }

  private _onPerformedGameAction(_: TSpinResponse): void {
    CoinAnimator.maxLandedCoinCount = 0;
    CoinAnimator.currentLandedCoinCount = 0;
    CoinAnimator.maxSplashCount = 0;
    CoinAnimator.currentSplashCount = 0;
    this._queuedCoinPositions = [];

    for (let i = 0; i < gameEngine.gameActionData.panelNew.length; ++i) {
      for (let j = 0; j < gameEngine.gameActionData.panelNew[i].length; ++j) {
        if (gameEngine.gameActionData.panelNew[i][j].startsWith("100")) {
          CoinAnimator.maxLandedCoinCount++;
          CoinAnimator.maxSplashCount++;
        }
      }
    }
  }

  private _onWinningPopupOpened(): void {
    CoinAnimator.shouldPlayCelebrationAnimation = true;

    // Ensure only big and above wins get the celebration animation.
    if (
      gameEngine.gameActionData &&
      gameEngine.gameActionData.odd > constants.BetToWinRatios.NORMAL
    ) {
      this._startCoinCelebrationAnimation();
    }
  }

  private _onWinningPopupClosed(): void {
    CoinAnimator.shouldPlayCelebrationAnimation = false;
  }

  private _startCoinCelebrationAnimation(): void {
    let completedCount: number = 0;
    let delayedCalls: GSAPTween[] = [];

    for (let i = 0; i < 15; ++i) {
      delayedCalls.push(
        gsap.delayedCall(Math.random() * 1.5, () => {
          if (!CoinAnimator.shouldPlayCelebrationAnimation) {
            delayedCalls.forEach((tween) => tween?.kill());
            delayedCalls = [];
            return;
          }

          console.log(`starting ${i} index coin celebration animation`);
          const coin: Spine = this._coinPool.acquire();
          coin.scale.set(1.5);
          coin.state.timeScale = 0.8;
          this._view.addChild(coin);

          this._createCoinCelebrationAnimation(coin);

          completedCount++;
          if (completedCount === 15) {
            delayedCalls.forEach((tween) => tween?.kill());
            delayedCalls = [];
          }
        }),
      );
    }
  }

  private _createCoinCelebrationAnimation(coin: Spine): void {
    const randomHorizontalDirection: number = Math.random() > 0.5 ? 1 : -1;
    const horizontalTravelDistance: number =
      (Math.random() * 100 + 400) * randomHorizontalDirection;
    const upwardTravelDistance: number = Math.random() * 50 + 450;

    const startX: number = (Math.random() * 2 - 1) * 400;
    const startY: number = 100 + (Math.random() * 2 - 1) * 400;

    coin.position.set(startX, startY);
    coin.rotation = Math.random() * Math.PI * 2;
    coin.scale.set(Math.min(Math.random() + 0.2, 1.0) + 0.5);

    const peakX: number = coin.position.x + horizontalTravelDistance * 0.5;
    const peakY: number = coin.position.y - upwardTravelDistance;

    const finalX: number = coin.position.x + horizontalTravelDistance;
    const finalY: number = Math.random() * 300 + 300;

    gsap.to(coin, {
      duration: 2.0,
      rotation: Math.random() * Math.PI * 2,
      motionPath: {
        path: [
          { x: startX, y: startY },
          { x: peakX, y: peakY },
          { x: finalX, y: finalY },
        ],
        curviness: 1,
        // autoRotate: true,
      },
      ease: "none",
      onComplete: () => {
        if (!CoinAnimator.shouldPlayCelebrationAnimation) {
          this._coinPool.release(coin);
          console.log("stopping coin celebration animation");
          return;
        }

        coin.position.set(0, -200);
        coin.scale.set(0.9);

        this._createCoinCelebrationAnimation(coin);
      },
    });
  }

  private _startCoinFlyingAnimation(startPosition: PIXI.Point): void {
    Audio.play("sndBonusCoinCollect", false, 0.6);
    const coin = this._coinPool.acquire();
    coin.position.set(startPosition.x, startPosition.y);

    this._view.addChild(coin);

    const endPosition =
      this._coinFlyEndpoints[
      Math.floor(Math.random() * this._coinFlyEndpoints.length)
      ];

    const middlePosition = new PIXI.Point(
      startPosition.x,
      Math.floor(Math.random() * (-550 - -600 + 1)) + -600,
    );

    gsap.to(coin, {
      duration: 0.75,
      motionPath: {
        path: [
          { x: startPosition.x, y: startPosition.y }, // start position
          { x: middlePosition.x, y: middlePosition.y }, // go up
          { x: endPosition.x, y: endPosition.y }, // fall a bit down
        ],
        curviness: 1.0,
        autoRotate: true,
      },
      ease: "power1.in",
      onComplete: () => {
        this._coinPool.release(coin);

        const splash = this._coinSplashPool.acquire();
        splash.position.set(coin.position.x, coin.position.y + 20);
        splash.scale.set(this._coinFlySplashScale);

        Audio.stop("sndBonusCoinLand");
        Audio.play("sndBonusCoinLand", false, 0.05);
        this._view.addChild(splash);
      },
    });

    gsap.to(coin.scale, {
      x: this._coinFlyEndScale,
      y: this._coinFlyEndScale,
      duration: 0.75,
      ease: "power1.in",
    });
  }

  private _isLandscape!: boolean;

  private _startCoinToCounter(x: number, y: number): void {
    this._animateHoldCoins(x, y);
  }

  private _animateHoldCoins(x: number, y: number, coinCount: number = 5): void {
    const scatterRadius = 40; // How far they spread from the center

    for (let i = 0; i < coinCount; i++) {
      // Acquire from pool
      const coin = this._coinPool.acquire();
      this._view.addChild(coin);

      // 1. Convert center point to local
      const centerLocalPos = this._view.toLocal(new PIXI.Point(x, y));

      // 2. Add random scatter so they don't overlap perfectly at the start
      const startX = centerLocalPos.x + (Math.random() * scatterRadius * 2 - scatterRadius);
      const startY = centerLocalPos.y + (Math.random() * scatterRadius * 2 - scatterRadius);

      coin.position.set(startX, startY);
      coin.scale.set(1.0); // Reset scale

      const counterEndpoint: PIXI.PointData = !this._isLandscape
        ? { x: -5.0, y: 450.0 }
        : { x: -5.0, y: -420.0 };

      // 3. Randomize the arc and duration slightly for each coin to create "rain" depth
      const randomDelay = i * 0.05; // Staggered start times
      const duration = 0.6 + Math.random() * 0.2;
      const controlX = (startX + counterEndpoint.x) / 2 + (Math.random() * 400 - 200);
      const controlY = Math.min(startY, counterEndpoint.y) - (100 + Math.random() * 100);

      // 4. Tween with staggered start
      gsap.to(coin, {
        delay: randomDelay,
        duration: duration,
        motionPath: {
          path: [
            { x: startX, y: startY },
            { x: controlX, y: controlY },
            { x: counterEndpoint.x, y: counterEndpoint.y }
          ],
          curviness: 1.8
        },
        ease: "power2.in",
        onStart: () => {
          // Optional: Play a tiny "clink" sound on start
        },
        onComplete: () => {
          this._coinPool.release(coin);
        }
      });

      // Shrink as they hit the meter
      gsap.to(coin.scale, {
        x: 0.5,
        y: 0.5,
        duration: duration,
        delay: randomDelay
      });
    }
  }
}

class CoinPool {
  private pool: Spine[] = [];

  constructor(initialPoolSize: number = 20) {
    for (let i = 0; i < initialPoolSize; i++) {
      this.pool.push(this._createCoin());
    }
  }

  public acquire(): Spine {
    if (this.pool.length === 0) {
      return this._createCoin();
    }

    const coin = this.pool.pop()!;

    this._resetCoin(coin);

    return coin;
  }

  public release(coin: Spine) {
    requestAnimationFrame(() => {
      coin.removeFromParent();
      this.pool.push(coin);
    });
  }

  private _createCoin(): Spine {
    const coin = Spine.from({
      skeleton: "coinRotateAtlasJson",
      atlas: "coinRotateAtlas",
      scale: 0.1,
    });
    this._resetCoin(coin);

    return coin;
  }

  private _resetCoin(coin: Spine) {
    coin.skeleton.setSkinByName("default");
    coin.state.setAnimation(0, "rotate", true);
    coin.state.timeScale = 1;
    coin.scale.set(1);
    coin.position.set(0);
    coin.rotation = 0;
  }
}

// Auto-releases.
class CoinSplashPool {
  private pool: Spine[] = [];

  constructor(initialPoolSize: number = 20) {
    for (let i = 0; i < initialPoolSize; i++) {
      this.pool.push(this._createCoinSplash());
    }
  }

  public acquire(): Spine {
    const splash = this.pool.pop() || this._createCoinSplash();
    this._resetCoinSplash(splash);

    const listener = {
      complete: () => {
        this._release(splash);

        CoinAnimator.currentSplashCount++;

        if (CoinAnimator.currentSplashCount === CoinAnimator.maxSplashCount) {
          EventSystem.invoke(constants.GameEvents.COIN_SPLASH_STOPPED);
        }
      },
    };

    splash.state.addListener(listener);
    (splash as any)._poolListener = listener;

    return splash;
  }

  private _release(splash: Spine) {
    requestAnimationFrame(() => {
      const listener = (splash as any)._poolListener;
      if (listener) {
        splash.state.removeListener(listener);
        delete (splash as any)._poolListener;
      }

      splash.removeFromParent();
      this.pool.push(splash);
    });
  }

  private _createCoinSplash(): Spine {
    return Spine.from({
      skeleton: "coinSplashAtlasJson",
      atlas: "coinSplashAtlas",
      scale: 0.1,
    });
  }

  private _resetCoinSplash(splash: Spine) {
    splash.skeleton.setSkinByName("default");
    splash.state.setAnimation(0, "coin_splash", false);
    splash.state.timeScale = Math.random() + 2;
  }
}
